import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faChevronLeft, faChevronRight, faEllipsis, faHouse, faXmark } from "@fortawesome/free-solid-svg-icons";

import { UserMenu } from "./UserMenu";
import { NotificationMenu } from "./NotificationMenu";
import { QuickActions } from "./QuickActions";
import { GlobalSearch } from "./GlobalSearch";
import { LanguageSwitch } from "./LanguageSwitch";
import { ContractSwitch } from "./ContractSwitch";
import { resolvePage } from "./pageRegistry";
import { canOpen } from "../../permissions/registry";
import { useAccess } from "../../permissions/access";

/** زر الرجوع: الصفحة السابقة في التنقل، وإلا الصفحة الأب المنطقية للمسار، وإلا الرئيسية. */
function HeaderBackButton({ page, isRtl, label }) {
  const navigate = useNavigate();
  const goBack = () => {
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else if (page.parent && canOpen(page.parent)) navigate(page.parent);
    else navigate(canOpen("/home-page") ? "/home-page" : "/profile");
  };
  return (
    <button type="button" className="gh-back" onClick={goBack} aria-label={label} title={label} data-testid="gh-back">
      <FontAwesomeIcon icon={isRtl ? faArrowRight : faArrowLeft} />
      <span className="gh-back__label">{label}</span>
    </button>
  );
}

/** مسار الصفحة من بيانات المسار: الرئيسية ← القسم (العنوان في كتلة مستقلة بجانبه). */
function HeaderBreadcrumb({ page, isRtl, homeLabel, panel = false }) {
  const trail = [{ label: homeLabel, to: canOpen("/home-page") ? "/home-page" : undefined, icon: faHouse }];
  if (page.section) trail.push({ label: page.section, to: page.sectionTo && canOpen(page.sectionTo) ? page.sectionTo : undefined });
  return (
    <nav className={panel ? "gh-crumbs gh-crumbs--panel" : "gh-crumbs"} aria-label={homeLabel} data-testid={panel ? "gh-crumbs-panel" : "gh-crumbs"}>
      <ol>
        {trail.map((item, i) => (
          <li key={`${item.label}-${i}`}>
            {item.to ? (
              <Link to={item.to} className="gh-crumbs__link" title={item.label} aria-label={item.label}>{item.icon && <FontAwesomeIcon icon={item.icon} />}<span>{item.label}</span></Link>
            ) : (
              <span className="gh-crumbs__text" title={item.label}>{item.icon && <FontAwesomeIcon icon={item.icon} />}<span>{item.label}</span></span>
            )}
            {i < trail.length - 1 && <FontAwesomeIcon icon={isRtl ? faChevronLeft : faChevronRight} className="gh-crumbs__sep" />}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** عنوان الصفحة الحالية وأيقونتها (من بيانات المسار). */
function HeaderPageTitle({ page }) {
  return (
    <div className="gh-title" data-testid="gh-title">
      <span className="gh-title__icon"><i className={page.icon} aria-hidden="true" /></span>
      <h1 className="gh-title__text" title={page.title}>{page.title}</h1>
    </div>
  );
}

/**
 * الهيدر الموحّد للنظام — مستطيل واحد يُرسم مرة واحدة في الـLayout لكل الصفحات:
 * رجوع | الرئيسية › القسم | عنوان الصفحة | إجراء جديد | بحث | الإشعارات | اللغة | المستخدم.
 * عنوان الصفحة وأيقونتها ومسارها وصفحتها الأب تأتي من بيانات المسار (pageRegistry)،
 * والإجراءات والبحث حسب صلاحيات المستخدم. الاتجاه (RTL/LTR) يتبع اللغة تلقائياً.
 */
export function AppHeader() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  useAccess(); // يعيد الرسم عند تغيّر الصلاحيات (الروابط والإجراءات)
  const lang = i18n.language?.split("-")[0] || "ar";
  const isRtl = (i18n.dir?.() || "rtl") === "rtl";
  const page = useMemo(() => resolvePage(location.pathname, lang), [location.pathname, lang]);
  const [more, setMore] = useState(false);

  // عنوان تبويب المتصفح من نفس بيانات الصفحة، وإغلاق لوحة الجوال عند التنقل
  useEffect(() => {
    document.title = `${page.title} | ASF`;
  }, [page.title]);
  useEffect(() => setMore(false), [location.pathname]);

  const isHome = location.pathname.startsWith("/home-page");

  return (
    <header className={`gh ${more ? "gh--more" : ""}`} role="banner" dir={isRtl ? "rtl" : "ltr"} data-testid="app-header">
      <div className="gh__context">
        {!isHome && <HeaderBackButton page={page} isRtl={isRtl} label={t("common.back")} />}
        {!isHome && <span className="gh__sep" aria-hidden="true" />}
        <HeaderBreadcrumb page={page} isRtl={isRtl} homeLabel={t("breadcrumbs.home")} />
        <HeaderPageTitle page={page} />
      </div>

      <div className="gh__tools">
        <div className="gh__extra" data-testid="gh-extra">
          {/* المسار داخل لوحة الجوال فقط (مخفي على الشاشات الأكبر حيث يظهر في الشريط) */}
          <HeaderBreadcrumb page={page} isRtl={isRtl} homeLabel={t("breadcrumbs.home")} panel />
          <QuickActions />
          <GlobalSearch />
          <ContractSwitch />
          <LanguageSwitch />
        </div>
        <NotificationMenu />
        <button type="button" className="gh-more" onClick={() => setMore((m) => !m)} aria-expanded={more}
          aria-label={t("header.openMenu")} title={t("header.openMenu")} data-testid="gh-more">
          <FontAwesomeIcon icon={more ? faXmark : faEllipsis} />
        </button>
        <span className="gh__sep gh__sep--user" aria-hidden="true" />
        <UserMenu />
      </div>
    </header>
  );
}
